import React, { useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { Box } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { Typo } from "../text/Typo";
import { ITheme } from "../theme/GlobalTheme";

export interface IRadioItemProps<T> {
	value: T | null;
	onSelect?: () => void;
	selected?: boolean;
	size?: "small" | "medium" | "large";
}

interface ISelectProps<T = any> {
	id?: string;
	sx?: React.CSSProperties & { [key: string]: any };
	ignoreInput?: boolean;
	label?: string;
	name?: string;
	data?: Record<string, any>;
	value?: T;
	values?: readonly T[];
	onChange?: (v: T) => void;
	readOnly?: boolean;
	disabled?: boolean;
	size?: "small" | "medium" | "large";
}

const getDisplayName = (value: any) => {
	let displayValue = value ? "???" : "---";

	if ((typeof value === "string" && value) || typeof value === "number") displayValue = value + "";

	if (typeof value === "boolean") displayValue = value ? "YES" : "NO";

	if (typeof value === "object") {
		if (value && value.hasOwnProperty("name")) displayValue = (value as any)["name"];
		if (value && value.hasOwnProperty("label")) displayValue = (value as any)["label"];
		else if (value && value.hasOwnProperty("title")) displayValue = (value as any)["title"];
		else if (value && value.hasOwnProperty("displayName")) displayValue = (value as any)["displayName"];
	}

	return displayValue;
};

export const Radio = <T,>({ sx, label, name, data, values, onChange, value, disabled, size }: ISelectProps<T>) => {
	const [selectedValue, setValue] = useState<T | undefined>(value);
	if (!size) size = "medium";

	const effect = data ? [data[name ?? 1] ?? 1] : [];
	useEffect(() => {
		setValue(data && name ? data[name] : value);
	}, [effect]);

	const handleValueSelect = (v: T) => {
		const obj = v as any;
		if (typeof obj === "object" && "value" in obj && "name" in obj && Object.keys(obj).length === 2) {
			v = obj.value as T;
		}

		if (name && data) data[name] = v;
		setValue(v);
		if (onChange) onChange(v);
	};

	return (
		<Box
			sx={{
				position: "relative",
			}}>
			{label && (
				<>
					<Typo variant="medium">{label}:</Typo>
					<Spacer size="10px" />
				</>
			)}

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "1fr max-content",
					overflow: "hidden",
					position: "relative",
					...sx,
				}}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "8px", paddingLeft: "6px" }}>
					{values &&
						values.map((v: T, index: number) => {
							const props = {
								value: v,
								selected: v === selectedValue,
							};

							if (typeof v === "object" && typeof selectedValue !== "object") {
								for (let i in v) {
									if (v[i] === selectedValue) {
										props.value = v;
										props.selected = true;
										break;
									}
								}
							}

							return <RadioItem key={index} size={size} {...props} onSelect={() => !disabled && handleValueSelect(v)} />;
						})}
				</Box>
			</Box>
		</Box>
	);
};

const RadioItem = <T,>({ value, onSelect, selected, size }: IRadioItemProps<T>) => {
	let displayValue = getDisplayName(value);
	const tsx: Record<string, any> = {};
	if (!size) size = "medium";

	const theme = useTheme() as ITheme;

	if (selected) {
		tsx.pointerEvents = "none";
	}

	return (
		<Box
			onClick={e => {
				e!.preventDefault();
				e!.stopPropagation();
				onSelect && onSelect();
			}}
			sx={{
				cursor: selected ? "auto" : "pointer",
				display: "flex",
				alignItems: "center",
				flexDirection: "row",
				gap: size === "small" ? "6px" : size === "large" ? "16px" : "12px",
				...tsx,
				overflow: "hidden",
			}}>
			<Box
				sx={{
					width: size === "small" ? "12px" : size === "large" ? "22px" : "18px",
					height: size === "small" ? "12px" : size === "large" ? "22px" : "18px",
					borderRadius: "50%",
					boxSizing: "border-box",
					border: `1px solid ${theme.colors.inputBorderPrimary}`,
					flexShrink: 0,
					backgroundColor: theme.colors.panelBackgroundPrimary,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}>
				{selected && (
					<Box
						sx={{
							width: size === "small" ? "6px" : size === "large" ? "10px" : "8px",
							height: size === "small" ? "6px" : size === "large" ? "10px" : "8px",
							borderRadius: "50%",
							backgroundColor: `${theme.colors.buttonBackgroundPrimary}`,
							margin: "auto",
						}}
					/>
				)}
			</Box>
			<Typo
				variant="medium"
				sx={{
					textOverflow: "ellipsis",
					overflow: "hidden",
					whiteSpace: "nowrap",
				}}>
				{displayValue}
			</Typo>
		</Box>
	);
};
